/**
 * Masked Input Component for Number Formatting
 */

import type { InputHTMLAttributes } from "react";
import { forwardRef } from "react";
import { NumericFormat } from "react-number-format";
import { UpDownIcon } from "@/assets/svg";

export interface MaskedNumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  readonly errorMessage?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

export const MaskedNumberInput = forwardRef<
  HTMLInputElement,
  MaskedNumberInputProps
>(
  (
    {
      errorMessage,
      className,
      id,
      disabled,
      value,
      onChange,
      placeholder,
    },
    ref
  ): React.JSX.Element => {
    const helperId = errorMessage ? `${id ?? "input"}-error` : undefined;

    const baseClasses =
      "w-full px-3 py-2 rounded-lg border text-sm leading-6 text-gray-900 bg-white transition-all duration-150 ease-in-out placeholder:text-gray-400 focus:outline-none";
    const normalClasses =
      "border-gray-200 focus:border-[#001a41] focus:shadow-[0_0_0_1px_rgba(0,26,65,0.08)]";
    const errorClasses = "border-red-600";
    const disabledClasses = "bg-gray-100 text-gray-500 cursor-not-allowed";
    // Hide native spin buttons
    const hideSpinButtonClasses =
      "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

    const classes = [
      baseClasses,
      errorMessage ? errorClasses : normalClasses,
      disabled ? disabledClasses : "",
      hideSpinButtonClasses,
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    const handleNumberChange = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;

      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const clickY = event.clientY - rect.top;
      const delta = clickY < rect.height / 2 ? 1 : -1;

      const currentValue = parseFloat(value) || 0;
      const newValue = Math.max(0, currentValue + delta);

      onChange(newValue.toString());
    };

    return (
      <div className="flex flex-col gap-1">
        <div className="relative">
          <NumericFormat
            id={id}
            value={value}
            onValueChange={(values) => {
              onChange(values.value);
            }}
            thousandSeparator="."
            decimalSeparator=","
            allowNegative={false}
            disabled={disabled}
            placeholder={placeholder}
            className={classes}
            getInputRef={ref}
            aria-invalid={errorMessage ? true : undefined}
            aria-describedby={helperId}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={handleNumberChange}
              onMouseDown={(e) => e.preventDefault()}
              disabled={disabled}
              className="bg-transparent hover:border-none p-0 m-0 flex flex-col items-center justify-center h-10 w-5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Increment/Decrement"
            >
              <UpDownIcon />
            </button>
          </div>
        </div>
        {errorMessage && (
          <p id={helperId} className="text-xs text-red-600">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

MaskedNumberInput.displayName = "MaskedNumberInput";
